// Cookie consent types and constants

export const COOKIE_CONSENT_VERSION = '1.0.0';
export const CONSENT_STORAGE_KEY = 'lookbel_cookie_consent';

export type ConsentCategory = 'necessary' | 'analytics' | 'functional' | 'marketing';

export interface ConsentCategories {
  necessary: true; // Always true — cannot be disabled
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
}

export interface ConsentState {
  consentGiven: boolean;
  version: string;
  timestamp: string;
  categories: ConsentCategories;
}

export interface CookieConsentContextValue {
  consentState: ConsentState | null;
  hasConsent: (category: ConsentCategory) => boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  savePreferences: (categories: Omit<ConsentCategories, 'necessary'>) => void;
  resetConsent: () => void;
  openPreferences: () => void;
  isPreferencesOpen: boolean;
  closePreferences: () => void;
  isBannerVisible: boolean;
}

// Category metadata for UI rendering
export interface CategoryMeta {
  id: ConsentCategory;
  title: string;
  description: string;
  required: boolean;
}

export const CONSENT_CATEGORIES: CategoryMeta[] = [
  {
    id: 'necessary',
    title: 'Strictly Necessary',
    description:
      'These cookies are essential for the website to work. They enable core functionality such as authentication, security, and session management. You cannot opt out of these.',
    required: true,
  },
  {
    id: 'analytics',
    title: 'Analytics',
    description:
      'These cookies help us understand how visitors use the site — which pages are visited most, how users navigate, and where improvements can be made. All data is aggregated and anonymous.',
    required: false,
  },
  {
    id: 'functional',
    title: 'Functional',
    description:
      'These cookies remember your preferences and settings to enhance your experience — such as your location, language, and referral information.',
    required: false,
  },
  {
    id: 'marketing',
    title: 'Marketing',
    description:
      'These cookies are used to deliver relevant content and track the effectiveness of marketing campaigns. They may be shared with trusted advertising partners.',
    required: false,
  },
];

// Developer mapping: which integrations belong to which category
// Update this as new tools are added
export const INTEGRATION_CATEGORY_MAP: {
  name: string;
  category: ConsentCategory;
  essential: boolean;
  loadedAt: string;
}[] = [
  {
    name: 'React Router / Session',
    category: 'necessary',
    essential: true,
    loadedAt: 'App boot',
  },
  {
    name: 'Stripe.js',
    category: 'necessary',
    essential: true,
    loadedAt: 'Booking flow only',
  },
  {
    name: 'Referral agent cookie (document.cookie)',
    category: 'functional',
    essential: false,
    loadedAt: 'ReferralTrackingPage',
  },
  {
    name: 'Web Vitals (reportWebVitals)',
    category: 'analytics',
    essential: false,
    loadedAt: 'index.tsx — gated behind analytics consent',
  },
];
